import {
  APP_CONSTANTS,
  MESSAGING_API_CONSTANTS,
  CONVERSATION_CONSTANTS,
  SUPPORTED_ENTRY_TYPES,
} from './constants';
import {
  getOrganizationId,
  getDeploymentDeveloperName,
  getSalesforceMessagingUrl,
  getJwt,
  clearWebStorage,
} from './dataProvider';
import { v4 as uuidv4 } from 'uuid';
import type {
  GetAccessTokenResponse,
  GetConversationRoutingStatusResponse,
  ListConversationEntriesResponse,
  ListConversationsResponse,
  RoutingAttributes,
  ServerSentEvent,
} from './types';

/**
 * Send an HTTP request using fetch with a specified path, method, mode, headers, and body.
 *
 * @param {String} apiPath - Endpoint to make request to.
 * @param {String} method - HTTP request method (POST, GET, DELETE).
 * @param {String} mode - HTTP mode (cors, no-cors, same-origin, navigate).
 * @param {Object} requestHeaders - Headers to include with request.
 * @param {Object} requestBody - Body to include with request. This method stringifies the object passed in, except when
 *                               uploading a file. For file attachments, request body must be binary data.
 * @returns {Promise}
 */
function sendFetchRequest<ResponseType extends Record<string, unknown>>(
  apiPath: string,
  method: string,
  mode: RequestMode,
  requestHeaders: Record<string, string> | null,
  requestBody: unknown,
): Promise<Response & ResponseType> {
  const messagingJwt = getJwt();
  const headers = requestHeaders
    ? requestHeaders
    : {
        'Content-Type': 'application/json',
        ...(messagingJwt && { Authorization: 'Bearer ' + messagingJwt }),
      };
  const body = requestBody ? JSON.stringify(requestBody) : undefined;

  return fetch(apiPath, {
    headers,
    method,
    mode,
    ...(body && { body }),
  }).then(async response => {
    if (response.status === 401) {
      // Unauthorized request. Clear the web storage.
      clearWebStorage();
    }
    if (!response.ok) {
      let responseObject;
      try {
        responseObject = Object.assign(response, await response.json());
      } catch (e) {
        responseObject = Object.assign(response, {
          message: `Error reading the body stream of error object: ${response}`,
        });
      }
      throw responseObject;
    }
    return response as Response & ResponseType;
  });
}

/**
 * Get a JWT for an anonymous user. This JWT is used for unauthenticated conversations.
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=generateAccessTokenForUnauthenticatedUser
 *
 * @returns {Promise}
 */
function getUnauthenticatedAccessToken() {
  const orgId = getOrganizationId();
  const esDeveloperName = getDeploymentDeveloperName();
  const messagingUrl = getSalesforceMessagingUrl();
  const capabilitiesVersion = APP_CONSTANTS.APP_CAPABILITIES_VERSION;
  const platform = APP_CONSTANTS.APP_PLATFORM;
  const apiPath = `${messagingUrl}/iamessage/api/v2/authorization/unauthenticated/access-token`;

  return sendFetchRequest<GetAccessTokenResponse>(
    apiPath,
    'POST',
    'cors',
    {
      'Content-Type': 'application/json',
    },
    {
      capabilitiesVersion,
      esDeveloperName,
      orgId,
      platform,
    },
  ).then(response => {
    if (!response.ok) {
      throw response;
    }
    return response.json() as Promise<GetAccessTokenResponse>;
  });
}

function getAuthenticatedAccessToken(customerIdentityToken: string) {
  const orgId = getOrganizationId();
  const esDeveloperName = getDeploymentDeveloperName();
  const messagingUrl = getSalesforceMessagingUrl();
  const capabilitiesVersion = APP_CONSTANTS.APP_CAPABILITIES_VERSION;
  const platform = APP_CONSTANTS.APP_PLATFORM;
  const apiPath = `${messagingUrl}/iamessage/api/v2/authorization/authenticated/access-token`;

  return sendFetchRequest<GetAccessTokenResponse>(
    apiPath,
    'POST',
    'cors',
    {
      'Content-Type': 'application/json',
    },
    {
      authorizationType: 'JWT',
      capabilitiesVersion,
      customerIdentityToken,
      esDeveloperName,
      orgId,
      platform,
    },
  ).then(response => {
    if (!response.ok) {
      throw response;
    }
    return response.json() as Promise<GetAccessTokenResponse>;
  });
}

/**
 * Create a new conversation.
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=createConversation
 *
 * @param {Object} routingAttributes - Optional. Prechat data to be used while routing the conversation request.
 * @returns {Promise}
 */
function createConversation(
  conversationId: string,
  routingAttributes?: RoutingAttributes,
) {
  const esDeveloperName = getDeploymentDeveloperName();
  const messagingUrl = getSalesforceMessagingUrl();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation`;

  return sendFetchRequest(apiPath, 'POST', 'cors', null, {
    ...(routingAttributes && { routingAttributes }),
    conversationId,
    esDeveloperName,
  }).then(response => {
    if (!response.ok) {
      throw response;
    }
  });
}

/**
 * Get a JWT with the same subjectId but new clientId as the existing Messaging JWT that was issued previously. This function is used for session continuity in the same tab (page reload) and/or across tabs.
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=generateContinuationToken
 *
 * @returns {Promise}
 */
function getContinuityJwt() {
  const messagingUrl = getSalesforceMessagingUrl();
  const apiPath = `${messagingUrl}/iamessage/api/v2/authorization/continuation-access-token`;

  return sendFetchRequest(apiPath, 'GET', 'cors', null, null).then(response => {
    if (!response.ok) {
      throw response;
    }
    return response.json() as Promise<{ accessToken: string }>;
  });
}

/**
 * Get a list of all conversations the current subjectId is participating in.
 * Returns:
 * - number of open conversations
 * - number of closed conversations
 * - array of conversations
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=listConversations
 *
 * @param {Boolean} includeClosedConversations - Whether to include closed conversations in list. Optional.
 * @returns {Promise}
 */
function listConversations(includeClosedConversations = false) {
  const messagingUrl = getSalesforceMessagingUrl();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/list?inclClosedConvs=${includeClosedConversations}&limit=${MESSAGING_API_CONSTANTS.LIST_CONVERSATION_API_NUM_CONVERSATIONS_LIMIT}`;

  return sendFetchRequest<ListConversationsResponse>(
    apiPath,
    'GET',
    'cors',
    null,
    null,
  ).then(response => {
    if (!response.ok) {
      throw response;
    }
    return response.json() as Promise<ListConversationsResponse>;
  });
}

/**
 * Get a list of conversation entries for a given conversationId.
 * Returns:
 * - array of conversation entries
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=listConversationEntries
 *
 * @param {String} conversationId - The ID of the conversation to get entries for.
 * @param {Number} startTimestamp - The start time for the window of time being requested. Optional.
 * @param {Number} endTimestamp - The end time for the window of time being requested. Optional.
 * @param {String} direction - Query direction either "FromStart" or "FromEnd" of the conversation. Optional.
 * @returns {Promise}
 */
function listConversationEntries(
  conversationId: string,
  startTimestamp?: number,
  endTimestamp?: number,
  direction?: string,
): Promise<ServerSentEvent[]> {
  const messagingUrl = getSalesforceMessagingUrl();
  const limitUrlQueryParam = `${MESSAGING_API_CONSTANTS.LIST_CONVERSATION_ENTRIES_API_ENTRIES_LIMIT ? `limit=${MESSAGING_API_CONSTANTS.LIST_CONVERSATION_ENTRIES_API_ENTRIES_LIMIT}` : ''}`;
  const startTimestampUrlQueryParam = `${startTimestamp ? `&startTimestamp=${startTimestamp}` : ''}`;
  const endTimestampUrlQueryParam = `${endTimestamp ? `&endTimestamp=${endTimestamp}` : ''}`;
  const directionUrlQueryParam = `${direction ? `&direction=${direction}` : ''}`;
  const entryTypeFilterUrlQueryParam = `${SUPPORTED_ENTRY_TYPES ? `&entryTypeFilter=${SUPPORTED_ENTRY_TYPES}` : ''}`;
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/entries?${limitUrlQueryParam}${startTimestampUrlQueryParam}${endTimestampUrlQueryParam}${directionUrlQueryParam}${entryTypeFilterUrlQueryParam}`;

  return sendFetchRequest<ListConversationEntriesResponse>(
    apiPath,
    'GET',
    'cors',
    null,
    null,
  )
    .then(response => {
      if (!response.ok) {
        throw response;
      }
      return response.json() as Promise<ListConversationEntriesResponse>;
    })
    .then(responseJson => {
      // Transform the response to look like a conversation entry received via a server-sent event.
      const transformedData: ServerSentEvent[] = [];

      if (typeof responseJson !== 'object' || responseJson === null) {
        throw new Error(
          `Expected to receive JSON response, instead received ${responseJson}.`,
        );
      }
      if (!Array.isArray(responseJson.conversationEntries)) {
        throw new Error(
          `Expected entries to be an Array, instead was: ${responseJson.conversationEntries}.`,
        );
      }
      responseJson.conversationEntries.forEach(conversationEntry => {
        conversationEntry.entryPayload = JSON.stringify(
          conversationEntry.entryPayload,
        );
        transformedData.push({
          conversationEntry,
          conversationId,
        });
      });

      return transformedData;
    });
}

/*
 * Publish a text message to a conversation.
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=sendMessage
 *
 * @param {String} conversationId - ID of conversation to send a text message to.
 * @param {String} text - String content to send to conversation.
 * @param {String} messageId - ID of the conversation entry.
 * @param {String} inReplyToMessageId - ID of message this message is a response for.
 * @param {boolean} isNewMessagingSession - Optional. Whether this message should create a new session. Used by session pre-chat forms.
 * @param {Object} routingAttributes - Optional. Pre-chat data to be used while routing the new session request. Used by session pre-chat forms.
 * @param {String} language - Optional. TODO.
 * @returns {Promise}
 */
function sendTextMessage(
  conversationId: string,
  text: string,
  messageId: string,
  inReplyToMessageId?: string,
  isNewMessagingSession?: boolean,
  routingAttributes?: RoutingAttributes,
  language?: string | null,
) {
  const messagingUrl = getSalesforceMessagingUrl();
  const esDeveloperName = getDeploymentDeveloperName();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/message`;

  return sendFetchRequest(apiPath, 'POST', 'cors', null, {
    message: {
      ...(inReplyToMessageId && { inReplyToMessageId }),
      id: messageId,
      messageType: CONVERSATION_CONSTANTS.MessageTypes.STATIC_CONTENT_MESSAGE,
      staticContent: {
        formatType: CONVERSATION_CONSTANTS.FormatTypes.TEXT,
        text,
      },
    },
    ...(routingAttributes && { routingAttributes }),
    ...(isNewMessagingSession && { isNewMessagingSession }),
    esDeveloperName,
    ...(language && { language }),
  }).then(response => {
    if (!response.ok) {
      throw response;
    }
    response.json();
  });
}

function sendChoiceMessage(
  conversationId: string,
  selectedOption: string,
  messageId: string,
  inReplyToMessageId: string,
  isNewMessagingSession?: boolean,
  routingAttributes?: RoutingAttributes,
  language?: string | null,
) {
  const messagingUrl = getSalesforceMessagingUrl();
  const esDeveloperName = getDeploymentDeveloperName();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/message`;

  return sendFetchRequest(apiPath, 'POST', 'cors', null, {
    message: {
      ...(inReplyToMessageId && { inReplyToMessageId }),
      choicesResponse: {
        formatType: 'Selections',
        selectedOptions: [{ optionIdentifier: selectedOption }],
      },
      id: messageId,
      messageType: CONVERSATION_CONSTANTS.MessageTypes.CHOICES_RESPONSE_MESSAGE,
    },
    ...(routingAttributes && { routingAttributes }),
    ...(isNewMessagingSession && { isNewMessagingSession }),
    esDeveloperName,
    ...(language && { language }),
  }).then(response => {
    if (!response.ok) {
      throw response;
    }
    response.json();
  });
}

function sendForm(
  conversationId: string,
  inputValues: ({
    inputId: string;
  } & (
    | {
        inputValueType: 'SelectedOptionsInputValue';
        selectedOptions: { optionIdentifier: string }[];
      }
    | {
        inputValueType: 'SingleInputValue';
        value:
          | {
              textValue: string;
              valueType: 'TextValue';
            }
          | {
              integerValue: number;
              valueType: 'IntegerValue';
            }
          | {
              dateValue: string;
              valueType: 'DateValue';
            }
          | {
              dateTimeValue: string;
              valueType: 'DateTimeValue';
            }
          | {
              doubleValue: number;
              valueType: 'DoubleValue';
            }
          | {
              urlValue: string;
              valueType: 'UrlValue';
            };
      }
  ))[],
  messageId: string,
  inReplyToMessageId: string,
  isNewMessagingSession?: boolean,
  routingAttributes?: RoutingAttributes,
  language?: string | null,
) {
  const messagingUrl = getSalesforceMessagingUrl();
  const esDeveloperName = getDeploymentDeveloperName();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/message`;

  return sendFetchRequest(apiPath, 'POST', 'cors', null, {
    message: {
      ...(inReplyToMessageId && { inReplyToMessageId }),
      formResponse: {
        formatType: 'Inputs',
        inputValues,
      },
      id: messageId,
      messageType: 'FormResponseMessage',
    },
    ...(routingAttributes && { routingAttributes }),
    ...(isNewMessagingSession && { isNewMessagingSession }),
    esDeveloperName,
    ...(language && { language }),
  }).then(response => {
    if (!response.ok) {
      throw response;
    }
    response.json();
  });
}

/**
 * https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=sendFile
 */
function sendFile(
  conversationId: string,
  file: File,
  messageId: string,
  fileId: string,
  inReplyToMessageId?: string,
  isNewMessagingSession?: boolean,
  routingAttributes?: RoutingAttributes,
  language?: string | null,
) {
  const messagingUrl = getSalesforceMessagingUrl();
  const esDeveloperName = getDeploymentDeveloperName();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/file`;

  const messagingJwt = getJwt();
  const headers = {
    ...(messagingJwt && { Authorization: 'Bearer ' + messagingJwt }),
  };

  const formData = new FormData();

  const messageEntryBlob = new Blob(
    [
      JSON.stringify({
        message: {
          ...(inReplyToMessageId && { inReplyToMessageId }),
          fileId,
          id: messageId,
        },
        ...(routingAttributes && { routingAttributes }),
        ...(isNewMessagingSession && { isNewMessagingSession }),
        esDeveloperName,
        ...(language && { language }),
      }),
    ],
    { type: 'application/json' },
  );

  formData.append('messageEntry', messageEntryBlob);
  formData.append('fileData', file);

  return fetch(apiPath, {
    body: formData,
    headers,
    method: 'POST',
    mode: 'cors',
  })
    .then(async response => {
      if (response.status === 401) {
        // Unauthorized request. Clear the web storage.
        clearWebStorage();
      }
      if (!response.ok) {
        let responseObject;
        try {
          responseObject = Object.assign(response, await response.json());
        } catch (e) {
          responseObject = Object.assign(response, {
            message: `Error reading the body stream of error object: ${response}`,
          });
        }
        throw responseObject;
      }
      return response as Response & ResponseType;
    })
    .then(response => {
      if (!response.ok) {
        throw response;
      }
      response.json();
    });
}

/**
 * Publish a started/stopped typing indicator entry to a conversation.
 *
 * @param {string} conversationId - ID of conversation to send a typing indicator to.
 * @param {string} typingIndicator - Indicate whether to typing started or stopped indicator.
 * @returns {Promise}
 */
function sendTypingIndicator(
  conversationId: string,
  typingIndicator:
    | typeof CONVERSATION_CONSTANTS.EntryTypes.TYPING_STARTED_INDICATOR
    | typeof CONVERSATION_CONSTANTS.EntryTypes.TYPING_STOPPED_INDICATOR,
) {
  const messagingUrl = getSalesforceMessagingUrl();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/entry`;

  return sendFetchRequest(apiPath, 'POST', 'cors', null, {
    entryType: typingIndicator,
    id: uuidv4(),
  });
}

/**
 * Close conversation and clean up JWT:
 * - clear JWT variable on inAppService.
 * - remove JWT from web storage (if web storage is available).
 *
 * This endpoint is typically used for anonymous users.
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=closeConversation
 *
 * @param {String} conversationId - ID of the conversation to close. Required.
 * @returns {Promise}
 */
function closeConversation(conversationId: string) {
  const messagingUrl = getSalesforceMessagingUrl();
  const esDeveloperName = getDeploymentDeveloperName();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}?esDeveloperName=${esDeveloperName}`;

  return sendFetchRequest(apiPath, 'DELETE', 'cors', null, null);
}

function sendMessageReadReceipt({
  conversationEntryId,
  conversationId,
}: {
  conversationEntryId: string;
  conversationId: string;
}) {
  const messagingUrl = getSalesforceMessagingUrl();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/acknowledge-entries`;

  return sendFetchRequest(apiPath, 'POST', 'cors', null, {
    acks: [
      {
        conversationEntryId: conversationEntryId,
        entryType: CONVERSATION_CONSTANTS.EntryTypes.READ,
        id: uuidv4(),
      },
    ],
  });
}

/**
 * Retrieve the routing status of the conversation. This function is used after we receive a `CONVERSATION_PARTICIPANT_CHANGED`
 * remove event to determine if the chat was ended or not.
 *
 * `TRANSFER` - Chat is transferred
 *
 * `NEEDS_ROUTING` - Chat is ended
 *
 * `ROUTED` - Chat is in a conference and one agent leaves
 *
 * https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=retrieveConversationRoutingStatus
 */
function getConversationRoutingStatus({
  conversationId,
}: {
  conversationId: string;
}) {
  const messagingUrl = getSalesforceMessagingUrl();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}`;

  return sendFetchRequest<GetConversationRoutingStatusResponse>(
    apiPath,
    'GET',
    'cors',
    null,
    null,
  ).then(response => {
    if (!response.ok) {
      throw response;
    }
    return response.json() as Promise<GetConversationRoutingStatusResponse>;
  });
}

export {
  getAuthenticatedAccessToken,
  getUnauthenticatedAccessToken,
  createConversation,
  getContinuityJwt,
  listConversations,
  listConversationEntries,
  sendTextMessage,
  sendChoiceMessage,
  sendForm,
  sendFile,
  sendTypingIndicator,
  closeConversation,
  sendMessageReadReceipt,
  getConversationRoutingStatus,
};
